import { useRef, useState, useEffect } from "react";
import { Clock, Droplets } from "lucide-react";
import { WeatherIcon } from "./WeatherIcon";
import { temp, tempUnit, formatHour } from "../lib/weather";

const useWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => setW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

const Sparkline = ({ values, pops, width, accent }) => {
  const H = 150;
  const padX = 24;
  const padY = 22;
  const barMax = 42;
  const lineBottom = H - barMax - 10;
  const n = values.length;
  if (!width || n < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = (width - padX * 2) / (n - 1);
  const barW = Math.max(3, stepX * 0.55);
  const pts = values.map((v, i) => {
    const x = padX + i * stepX;
    const y = padY + (1 - (v - min) / range) * (lineBottom - padY * 2);
    return [x, y];
  });
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${pts[n - 1][0].toFixed(1)},${lineBottom} L${pts[0][0].toFixed(1)},${lineBottom} Z`;

  return (
    <svg width={width} height={H} className="block" data-testid="hourly-chart">
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.45" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="rainFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.55" />
        </linearGradient>
      </defs>
      <line x1={padX} x2={width - padX} y1={H - 1} y2={H - 1} stroke="hsl(var(--border))" strokeWidth="1" />
      {pops.map((p, i) => {
        const h = (p / 100) * barMax;
        const x = pts[i][0] - barW / 2;
        return (
          <g key={i} data-testid={`rain-bar-${i}`}>
            <rect x={x} y={H - 1 - barMax} width={barW} height={barMax} rx="2" fill="hsl(var(--secondary))" opacity="0.5" />
            {h > 0 && <rect x={x} y={H - 1 - h} width={barW} height={h} rx="2" fill="url(#rainFill)" />}
            {p >= 40 && (
              <text x={pts[i][0]} y={H - 3 - barMax - 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#38BDF8" className="num">
                {`${p}%`}
              </text>
            )}
          </g>
        );
      })}
      <path d={area} fill="url(#sparkFill)" />
      <path d={line} fill="none" stroke={accent} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]} cy={p[1]} r="2.5" fill={accent} />
          <text x={p[0]} y={p[1] - 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="hsl(var(--foreground))" className="num">
            {values[i]}
          </text>
        </g>
      ))}
    </svg>
  );
};

export const HourlyTimeline = ({ hourly, offset, units, accent }) => {
  const [ref, width] = useWidth();
  const values = hourly.map((h) => temp(h.temp, units));
  const pops = hourly.map((h) => h.pop || 0);

  return (
    <div className="glass-card p-5 sm:p-6" data-testid="hourly-forecast-timeline">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Clock className="w-4 h-4 text-primary" />
        <h3 className="text-base font-semibold">Prakiraan Per Jam</h3>
        <span className="text-xs text-muted-foreground">48 jam ke depan · {tempUnit(units)}</span>
        <div className="ml-auto flex items-center gap-4 text-[11px] text-muted-foreground" data-testid="hourly-legend">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-4 h-0.5 rounded" style={{ background: accent }} />
            Suhu
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-3 rounded-sm bg-gradient-to-b from-sky-400 to-blue-500" />
            Peluang hujan
          </span>
        </div>
      </div>

      <div ref={ref} className="w-full overflow-hidden">
        <Sparkline values={values} pops={pops} width={width} accent={accent} />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3 pb-1">
        {hourly.map((h, i) => (
          <div
            key={h.dt}
            data-testid={`hourly-item-${i}`}
            className="shrink-0 w-[68px] rounded-2xl bg-secondary/40 hover:bg-secondary transition-colors p-3 text-center"
          >
            <div className="text-xs text-muted-foreground num">{formatHour(h.dt, offset)}</div>
            <WeatherIcon icon={h.condition.icon} className="w-7 h-7 mx-auto my-2" style={{ color: accent }} />
            <div className="text-sm font-bold num">
              {temp(h.temp, units)}
              {tempUnit(units)}
            </div>
            <div className="flex items-center justify-center gap-0.5 text-[10px] text-sky-500 mt-1 num">
              <Droplets className="w-3 h-3" />
              {h.pop}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
